import { Modal } from 'antd'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useForm } from './FormProvider'
import { RouteComponentProps, withRouter } from 'react-router-dom'

type TConfirmExit = {
  onOk?: () => void
  onCancel?: () => void
}

type TUnloadContext = {
  blockState: [boolean, React.Dispatch<React.SetStateAction<boolean>>]
  confirmUnload: (onOk: () => void) => void
}

const UnloadContext = createContext<TUnloadContext>({} as TUnloadContext)

export const useUnloadHandler = (): TUnloadContext => {
  const context = useContext(UnloadContext)
  if (!context) return {} as TUnloadContext
  return context
}

export const UnloadProvider = withRouter(({ children, history, location }: { children: React.ReactNode } & RouteComponentProps) => {
  const blockState = useState(false)
  const [shouldBlock] = blockState
  const { resetTouchedFields } = useForm()

  const confirmExit = ({ onOk, onCancel }: TConfirmExit) => {
    Modal.confirm({
      title: 'Leave page?',
      content: 'Unsaved changes may be lost.',
      okText: 'OK',
      cancelText: 'Cancel',
      zIndex: 100000,
      onOk,
      onCancel,
    })
  }

  const confirmUnload = useCallback(
    (onOk: () => void) => {
      if (shouldBlock)
        confirmExit({
          onOk: async () => {
            onOk()
            resetTouchedFields()
          }
        })
      else onOk()
    },
    [shouldBlock],
  )

  // TRIGGER KHI TRANG ĐƯỢC RELOAD
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlock) event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock]);

  // TRIGGER KHI URL CURRENT !== URL NEXT VÀ USER CÓ TÁC ĐỘNG TỚI FORM
  useEffect(() => {
    const unlisten = history.listen((nextLocation, action) => {
      const currentFullPath = location.pathname + location.search;
      const nextFullPath = nextLocation.pathname + nextLocation.search;
      if (currentFullPath !== nextFullPath && shouldBlock) {
        history.replace(currentFullPath)
        confirmExit({
          onOk: async () => {
            await resetTouchedFields()
            await history.push(nextFullPath)
          }
        })
      }
    });

    return () => {
      unlisten()
    }
  }, [shouldBlock, history, location.pathname, location.search])

  return (
    <UnloadContext.Provider value={{ blockState, confirmUnload }}>
      {children}
    </UnloadContext.Provider>
  )
})

export const FormUnloadWatcher = () => {
  const unloadHandler = useUnloadHandler()
  const { form } = useForm()

  useEffect(() => {
    if (unloadHandler.blockState) {
      const [_, setShouldBlock] = unloadHandler.blockState
      form?.isFieldsTouched() && setShouldBlock(true)
      return () => setShouldBlock(false)
    }

    return
  }, [form, form?.isFieldsTouched()])

  return <></>
}
